import { BooleanFormType } from "Store/interfaces/FormTypes";
import { HousePriceRequestInfo } from "Store/interfaces/products/checkPrice/riskProducts/HouseProducts/House";
import {
  formBooleanToBoolean,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";
import { assertExhaustive, isDefined } from "Utils/functions";
import {
  AdditionalBuilding,
  AdditionalBuildingType,
} from "../../AdditionalBuildings/additionalBuildingsInterfaces";
import { isFullRental, isPartialRental } from "../conditions";
import {
  AdditionalBuildingFormType,
  AdditionalBuildingFormValues,
  HouseProductDetailsFormValues,
  HouseProductInfoDetailsFormValues,
  HouseProductIntroFormValues,
} from "../houseFormValuesInterfaces";
import { HouseStandard } from "../houseTypes";

export const mapHouseFormsToPriceRequest = (
  formValues: HouseProductDetailsFormValues,
  introFormValues: HouseProductIntroFormValues,
  basicInfoFormValues: HouseProductInfoDetailsFormValues,
): HousePriceRequestInfo => ({
  ...mapToIntroPriceRequest(introFormValues),
  ...mapToInfoDetailsPriceRequest(basicInfoFormValues),
  standard: HouseStandard.NORMAL,
  ...(isDefined(formValues.hasBasementRoom) && {
    hasBasementRoom: formBooleanToBoolean(formValues.hasBasementRoom),
  }),
  hasPartialRental: isPartialRental(formValues.rentalOrUnoccupied),
  hasRental: isFullRental(formValues.rentalOrUnoccupied),
  isRoofChanged: nullableFormBooleanToNullableBoolean(formValues.isRoofChanged),
  isWetAreasRehabilitatedOrChanged: nullableFormBooleanToNullableBoolean(
    formValues.isWetAreasRehabilitatedOrChanged,
  ),
  isWithHistoryOfOfWaterOrMoistureDamage: nullableFormBooleanToNullableBoolean(
    formValues.isWithHistoryOfWaterOrMoistureDamage,
  ),
  pestsDamaged: nullableFormBooleanToNullableBoolean(formValues.pestsDamaged),
  injuries: Number(formValues.injuries),
  injuryYear: formValues.injuryYear ? Number(formValues.injuryYear) : undefined,
  hasPool: false,
  hasFireAlarm: formValues.hasFireAlarm,
  hasBurglarAlarm: formValues.hasBurglarAlarm,
  hasWaterGuard: formValues.hasWaterGuard,
  hasCircuitBreakers: formValues.hasCircuitBreakers,
  hasPipeInPipeSystem: formValues.hasPipeInPipeSystem,
});

const mapToIntroPriceRequest = (
  introFormValues: HouseProductIntroFormValues,
): Pick<
  HousePriceRequestInfo,
  "roofType" | "hasBasementRoom" | "hasFloodRisk" | "postNumber"
> => ({
  roofType: introFormValues.roofType,
  hasBasementRoom: introFormValues.hasBasementRoom,
  hasFloodRisk: introFormValues.hasFloodRisk,
  // TODO: Post number causes issues in test wrt. registered address,
  // since customer address and the address of an estate marked as registered
  // is not necessarily the same in test. Shouldn't cause any issues in production.
  ...(introFormValues.addressSameAsHomeAddress === BooleanFormType.TRUE
    ? { postNumber: undefined }
    : { postNumber: introFormValues.postNumber }),
});

const mapToInfoDetailsPriceRequest = (
  formValues: HouseProductInfoDetailsFormValues,
): Pick<
  HousePriceRequestInfo,
  "houseType" | "constructionYear" | "areal" | "otherBuildings"
> => ({
  houseType: formValues.houseType,
  constructionYear: formValues.constructionYear,
  areal: { bta: formValues.bta.toString(), boa: formValues.boa.toString() },
  otherBuildings: mapToOtherBuildingsPriceRequest(formValues.otherBuildings),
});

const mapToOtherBuildingsPriceRequest = (
  buildings: AdditionalBuildingFormValues[],
): AdditionalBuilding[] => {
  return buildings.map((building) => ({
    type: mapFromBuildingFormType(building.type),
    areal: String(building.areal),
    constructionYear: building.constructionYear,
    isolated: mapBuildingIsolation(building.type),
  }));
};
export function mapFromBuildingFormType(
  buildingType: AdditionalBuildingFormType,
): AdditionalBuildingType {
  switch (buildingType) {
    case AdditionalBuildingFormType.GARAGE_ISOLATED:
    case AdditionalBuildingFormType.GARAGE_NON_ISOLATED:
      return AdditionalBuildingType.GARAGE;
    case AdditionalBuildingFormType.OUTHOUSE_ISOLATED:
    case AdditionalBuildingFormType.OUTHOUSE_NON_ISOLATED:
      return AdditionalBuildingType.OUTHOUSE;
    case AdditionalBuildingFormType.CARPORT:
      return AdditionalBuildingType.CARPORT;
    case AdditionalBuildingFormType.GARAGE_OUTHOUSE_ANNEX:
      return AdditionalBuildingType.GARAGE_OUTHOUSE_ANNEX;
    default:
      return assertExhaustive(
        buildingType,
        new Error(`Unmapped additional building form type: ${buildingType}.`),
      );
  }
}
export function mapBuildingIsolation(
  buildingType: AdditionalBuildingFormType,
): boolean | undefined {
  switch (buildingType) {
    case AdditionalBuildingFormType.GARAGE_ISOLATED:
    case AdditionalBuildingFormType.OUTHOUSE_ISOLATED:
      return true;
    case AdditionalBuildingFormType.GARAGE_NON_ISOLATED:
    case AdditionalBuildingFormType.OUTHOUSE_NON_ISOLATED:
      return false;
  }
  return undefined;
}
